import { FC, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { VideoDetails } from "../replayTypes";

interface VideoFilePickerProps {
    videoDetails: VideoDetails;
    onVideoSelected: (videoDetails: VideoDetails) => void;
    // Incrementing this value opens the native file picker (used by overlay buttons).
    requestNonce: number;
}

/**
 * Hidden file input that lets the user pick a local video file and reports a
 * playable object URL back to the replay tool. Ported from the legacy
 * VideoFilePickerDirective.
 */
const VideoFilePicker: FC<VideoFilePickerProps> = ({
    videoDetails,
    onVideoSelected,
    requestNonce,
}) => {
    const { t } = useTranslation();
    const inputRef = useRef<HTMLInputElement>(null);
    const compatibilityVideoRef = useRef<HTMLVideoElement>(null);
    const [errorMessage, setErrorMessage] = useState<string>("");
    // Tracks the active object URL so it can be revoked on re-pick / unmount.
    const objectUrlRef = useRef<string | null>(null);

    const revokeObjectUrl = () => {
        if (objectUrlRef.current) {
            (window.URL || (window as any).webkitURL).revokeObjectURL(
                objectUrlRef.current
            );
            objectUrlRef.current = null;
        }
    };

    // Open the picker whenever an overlay requests a (re)selection.
    useEffect(() => {
        if (requestNonce > 0) {
            inputRef.current?.click();
        }
    }, [requestNonce]);

    // Release the pinned video blob when the picker unmounts.
    useEffect(() => {
        return () => revokeObjectUrl();
    }, []);

    const playSelectedFile = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files && e.target.files[0];
        const video = compatibilityVideoRef.current;
        if (!file || !video) {
            return;
        }

        const canPlay = video.canPlayType(file.type); // 'probably' | 'maybe' | ''
        const isError = canPlay === "";
        if (isError) {
            setErrorMessage(t("performance.VideoFilePicker.error"));
            return;
        }
        setErrorMessage("");

        // Revoke any previously-created URL before pinning the new blob.
        revokeObjectUrl();
        const url = (window.URL || (window as any).webkitURL).createObjectURL(
            file
        );
        objectUrlRef.current = url;
        onVideoSelected({
            ...videoDetails,
            canPlay,
            type: file.type,
            url,
            isCalibrated: videoDetails.isCalibrated === true,
        });
    };

    return (
        <div className="file-directive">
            <input
                ref={inputRef}
                className="file-picker"
                type="file"
                accept="video/*"
                style={{ display: "none" }}
                onChange={playSelectedFile}
            />
            {errorMessage && <p className="video-message">{errorMessage}</p>}
            <video
                ref={compatibilityVideoRef}
                className="compatibility"
                style={{ display: "none" }}
            />
        </div>
    );
};

export default VideoFilePicker;
